import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment, useEffect, useState } from "react";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";
import { IStoreTiming, IStoreTimingUpdatePayload } from "../interface";
import { formatTimeInputValue, getStoreTimingDraft } from "../helpers";

interface IStoreTimingModalProps {
  timing: IStoreTiming | null;
  isSaving: boolean;
  onClose: () => void;
  onSave: (warehouseId: string, payload: IStoreTimingUpdatePayload) => Promise<boolean>;
}

const StoreTimingModal = ({ timing, isSaving, onClose, onSave }: IStoreTimingModalProps) => {
  const [openingTime, setOpeningTime] = useState<string>("");
  const [closingTime, setClosingTime] = useState<string>("");

  useEffect(() => {
    if (!timing) return;
    setOpeningTime(formatTimeInputValue(timing.opening_hour, timing.opening_minute));
    setClosingTime(formatTimeInputValue(timing.closing_hour, timing.closing_minute));
  }, [timing]);

  const { payload, error: validationError } = getStoreTimingDraft(openingTime, closingTime);

  const handleSave = async () => {
    if (!timing || validationError || isSaving) return;
    const success = await onSave(timing.warehouse_id, payload);
    if (success) onClose();
  };

  const renderTimeField = (id: string, label: string, value: string, onChange: (value: string) => void) => (
    <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3">
      <label htmlFor={id} className="text-xs font-medium text-black sm:w-[110px] sm:shrink-0">
        {label}
      </label>
      <input
        id={id}
        type="time"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={!!validationError}
        className={`h-11 w-full rounded-md border px-3 text-base tabular-nums text-slate-700 transition-colors focus:outline-none focus:ring-2 sm:h-9 sm:flex-1 sm:text-xs ${
          validationError
            ? "border-red-300 focus:border-red-400 focus:ring-red-100"
            : "border-slate-300 focus:border-[#193A6A] focus:ring-[#193A6A]/15"
        }`}
      />
    </div>
  );

  return (
    <Transition.Root show={!!timing} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={isSaving ? () => undefined : onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-3 sm:items-center sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-2"
            >
              <Dialog.Panel className="max-h-[92vh] w-full max-w-[500px] overflow-y-auto rounded-2xl border border-gray-150 bg-white shadow-xl">
                <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-3.5 sm:px-5 sm:py-4">
                  <Dialog.Title as="h2" className="min-w-0 text-sm font-semibold text-gray-900 sm:text-base">
                    <span className="block text-gray-900">Manage Quick Commerce Timing</span>
                    <span className="mt-0.5 block truncate text-xs font-bold text-[#193A6A] sm:text-sm">
                      {timing?.warehouse_name || timing?.warehouse_id}
                    </span>
                  </Dialog.Title>
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="shrink-0 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#193A6A]/40"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSave();
                  }}
                >
                  <div className="px-4 py-4 sm:px-5">
                    <div className="space-y-3">
                      {renderTimeField("store-opening-time", "Opening Time", openingTime, setOpeningTime)}
                      {renderTimeField("store-closing-time", "Closing Time", closingTime, setClosingTime)}
                    </div>

                    {validationError && (
                      <p role="alert" className="mt-3 text-[11px] font-medium leading-4 text-red-600">
                        {validationError}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col-reverse gap-2 rounded-b-2xl border-t border-gray-200 bg-gray-50/50 px-4 py-3.5 sm:flex-row sm:justify-end sm:gap-3 sm:px-5 sm:py-4">
                    <button
                      type="button"
                      onClick={onClose}
                      disabled={isSaving}
                      className={`${SECONDARY_BUTTON} min-h-[44px] w-full disabled:opacity-50 sm:min-h-0 sm:w-auto`}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={!!validationError || isSaving}
                      className={`${PRIMARY_BUTTON} min-h-[44px] w-full disabled:cursor-not-allowed disabled:bg-gray-300 sm:min-h-0 sm:w-auto`}
                    >
                      {isSaving ? "Saving..." : "Save"}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default StoreTimingModal;
